import React from "react";
import { Info, Sparkles, X } from "lucide-react";
import { TagChip } from "../../atoms/TagChip";
import type { TagChipTone } from "../../atoms/TagChip/TagChip.types";
import { PillButton } from "../../atoms/PillButton";
import { useDataStudioRecommendationsPanel } from "../../../hooks/useDataStudioRecommendationsPanel";
import type {
  DataStudioRecommendation,
  DataStudioRecommendationSeverity,
} from "../../../services/dataStudio/dataStudioRecommendations.types";
import {
  CardActions,
  CardMeta,
  CloseButton,
  ErrorMessage,
  Header,
  Heading,
  InfoRow,
  Panel,
  RecommendationCard,
  RecommendationCopy,
  RecommendationDescription,
  Recommendations,
  RecommendationTitle,
  RevealList,
  SkeletonCard,
  StateMessage,
  WorkspaceHeading,
  WorkspaceInfo,
} from "./DataStudioRecommendationsPanel.styles";
import type { DataStudioRecommendationsPanelProps } from "./DataStudioRecommendationsPanel.types";

const SEVERITY: Record<DataStudioRecommendationSeverity, { label: string; tone: TagChipTone }> = {
  high: { label: "High", tone: "restored" },
  medium: { label: "Medium", tone: "edited" },
  low: { label: "Low", tone: "neutral" },
};

/** The v1 write actions the API serves today; the rest render disabled. */
const isActionable = (item: DataStudioRecommendation): boolean =>
  item.action?.type === "add_note" && item.action.enabled;

export const DataStudioRecommendationsPanel: React.FC<DataStudioRecommendationsPanelProps> = ({
  connectionId,
  assetId,
  onClose,
}) => {
  const rec = useDataStudioRecommendationsPanel(connectionId, assetId);

  const renderCard = (item: DataStudioRecommendation) => {
    const action = item.action;
    const actionable = isActionable(item);
    const saved = rec.savedIds.includes(item.id);
    const saving = rec.savingId === item.id;
    const reason = action?.disabled_reason ?? rec.comingSoonLabel;
    return (
      <RecommendationCard key={item.id} $previewed={false}>
        <RecommendationCopy>
          <RecommendationTitle>
            <Sparkles aria-hidden="true" />
            {item.title}
          </RecommendationTitle>
          <RecommendationDescription>{item.message}</RecommendationDescription>
        </RecommendationCopy>
        <CardMeta>
          <TagChip label={SEVERITY[item.severity].label} tone={SEVERITY[item.severity].tone} size="xs" />
          {item.columns.map((column) => (
            <TagChip key={column.column_id} label={column.name} tone="subtle" size="xs" />
          ))}
        </CardMeta>
        {action ? (
          <CardActions>
            <PillButton
              variant="outline"
              size={28}
              disabled={!actionable || saving || saved}
              title={actionable ? undefined : reason}
              onClick={() => rec.saveAsNote(item)}
            >
              {saved ? "Saved" : saving ? "Saving…" : action.label}
            </PillButton>
            {rec.saveErrorId === item.id ? <ErrorMessage role="alert">Could not save the note.</ErrorMessage> : null}
          </CardActions>
        ) : null}
      </RecommendationCard>
    );
  };

  const renderBody = () => {
    if (rec.isLoading) {
      return (
        <div aria-busy="true" aria-label="Loading recommendations">
          <SkeletonCard />
        </div>
      );
    }
    if (rec.errorMessage) {
      return (
        <StateMessage role="alert">
          {rec.errorMessage}{" "}
          <PillButton variant="link" size={22} onClick={rec.retry}>
            Try again
          </PillButton>
        </StateMessage>
      );
    }
    if (rec.status === "not_profiled") {
      return <StateMessage>Profile this table to get recommendations.</StateMessage>;
    }
    if (rec.items.length === 0) {
      return <StateMessage>No issues found in this table's profile.</StateMessage>;
    }
    return <RevealList>{rec.items.map(renderCard)}</RevealList>;
  };

  return (
    <Panel aria-labelledby="data-studio-recommendations-title">
      <Header>
        <Heading id="data-studio-recommendations-title">
          <Sparkles aria-hidden="true" />
          AI recommendations
        </Heading>
        <CloseButton type="button" aria-label="Close AI recommendations" onClick={onClose}>
          <X aria-hidden="true" />
        </CloseButton>
      </Header>

      <Recommendations>{renderBody()}</Recommendations>

      <WorkspaceInfo>
        <WorkspaceHeading>
          <Info aria-hidden="true" />
          Workspace info
        </WorkspaceHeading>
        <InfoRow>
          <span>KG Version</span>
          <strong title={rec.comingSoonLabel}>—</strong>
        </InfoRow>
        <InfoRow>
          <span>Last updated</span>
          <strong title={rec.comingSoonLabel}>—</strong>
        </InfoRow>
      </WorkspaceInfo>
    </Panel>
  );
};

export default DataStudioRecommendationsPanel;
